import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import styles from './LegendManager.module.css';
import Button from '../Button/Button';
import { PlusIcon, TrashIcon, SaveIcon, ImportIcon } from '../Icon';

const LegendManager = ({ isVisible, onClose, onSave }) => {
    const [legendData, setLegendData] = useState({ plots: [], buildings: [], roads: [], markers: [] });
    const [activeTab, setActiveTab] = useState('plots');
    const fileInputRef = useRef(null);
    const [uploadIndex, setUploadIndex] = useState(null);

    useEffect(() => {
        if (isVisible) {
            fetch('/api/legend')
                .then(res => res.json())
                .then(data => {
                    // Process data to ensure lineType and hatch fields exist with appropriate defaults
                    const processedData = { ...data };
                    Object.keys(processedData).forEach(type => {
                        processedData[type] = (processedData[type] || []).map(item => {
                            // Initialize lineType and hatch if not present
                            if (item.lineType === undefined) {
                                item.lineType = type === 'roads' ? 'solid' : null;
                            }
                            if (item.hatch === undefined) {
                                item.hatch = (type === 'plots' || type === 'buildings') ? 'none' : null;
                            }
                            if (item.image_filename === undefined) {
                                item.image_filename = type === 'markers' ? '' : null;
                            }
                            return item;
                        });
                    });
                    setLegendData(processedData);
                })
                .catch(err => toast.error('Не удалось загрузить легенду'));
        }
    }, [isVisible]);

    const handleAddItem = () => {
        const newItem = {
            id: `${activeTab}_${Date.now()}`,
            name: 'Новая категория',
            color: '#cccccc',
            lineType: activeTab === 'roads' ? 'solid' : null,
            hatch: (activeTab === 'plots' || activeTab === 'buildings') ? 'none' : null,
            image_filename: activeTab === 'markers' ? null : undefined,
            image_url: activeTab === 'markers' ? null : undefined,
        };
        setLegendData(prev => ({
            ...prev,
            [activeTab]: [...prev[activeTab], newItem]
        }));
    };

    const handleItemChange = (index, field, value) => {
        const items = [...legendData[activeTab]];
        items[index] = { ...items[index], [field]: value };
        setLegendData(prev => ({ ...prev, [activeTab]: items }));
    };

    const handleRemoveItem = (index) => {
        if (window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
            const currentItems = [...legendData[activeTab]];
            const itemToRemove = currentItems[index];
            const updatedItems = currentItems.filter((_, i) => i !== index);
            setLegendData(prev => ({ ...prev, [activeTab]: updatedItems }));

            // Если это маркер с изображением, отправляем запрос на удаление файла
            if (activeTab === 'markers' && itemToRemove.image_filename) {
                fetch('/api/delete-image', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ filename: itemToRemove.image_filename })
                }).then(res => {
                    if (res.ok) {
                        toast.info('Файл изображения удален с сервера.');
                    } else {
                        toast.warn('Не удалось удалить файл изображения с сервера.');
                    }
                }).catch(() => toast.error('Ошибка при удалении файла изображения.'));
            }
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file || uploadIndex === null) return;

        // Сохраняем имя старого файла для удаления после успешной загрузки нового
        const oldFilename = legendData[activeTab][uploadIndex]?.image_filename;

        const formData = new FormData();
        formData.append('image', file);

        fetch('/api/upload/image', {
            method: 'POST',
            body: formData,
        })
            .then(res => {
                if (!res.ok) throw new Error('Ошибка загрузки изображения');
                return res.json();
            })
            .then(data => {
                handleItemChange(uploadIndex, 'image_url', data.url);
                handleItemChange(uploadIndex, 'image_filename', data.filename);
                toast.success('Изображение успешно загружено');

                // Если был старый файл, удаляем его
                if (oldFilename && oldFilename !== data.filename) {
                    fetch('/api/delete-image', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ filename: oldFilename })
                    }).then(res => {
                        if (res.ok) {
                            toast.info('Старый файл изображения удален.');
                        }
                    });
                }
            })
            .catch(err => {
                toast.error(err.message || 'Не удалось загрузить изображение');
            })
            .finally(() => {
                // Сбрасываем значение input, чтобы можно было загрузить тот же файл снова
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                setUploadIndex(null);
            });
    };



    const handleSaveChanges = () => {
        // Отправляем все данные легенды одним запросом
        fetch('/api/legend', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(legendData)
        })
            .then(res => {
                if (!res.ok) throw new Error('Server error');
                toast.success('Легенда успешно сохранена!');
                onSave(); // Вызываем колбэк для обновления данных в приложении
                onClose();
            })
            .catch(() => toast.error('Ошибка при сохранении легенды'));
    };

    if (!isVisible) return null;

    const tabs = [
        { id: 'plots', name: 'Участки' },
        { id: 'buildings', name: 'Здания' },
        { id: 'roads', name: 'Дороги' },
        { id: 'markers', name: 'Метки' },
    ];

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2>Менеджер легенды</h2>
                    <button onClick={onClose} className={styles.closeButton}>&times;</button>
                </div>
                <div className={styles.content}>
                    <div className={styles.tabs}>
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabContent}>
                        <div className={styles.listHeader}>
                            <span>{activeTab === 'markers' ? 'Иконка' : 'Цвет'}</span>
                            <span>Название категории</span>
                            <span>
                                {activeTab === 'roads' ? 'Тип линии' :
                                    activeTab === 'plots' || activeTab === 'buildings' ? 'Штриховка' :
                                        activeTab === 'markers' ? 'Загрузка' : ''}
                            </span>
                            <span className={styles.actionsHeader}>Действия</span>
                        </div>
                        {legendData[activeTab]?.map((item, index) => (
                            <div key={item.id} className={styles.listItem}>
                                {activeTab === 'markers' ? (
                                    <div className={styles.imagePreview}>
                                        {item.image_url && <img src={item.image_url} alt={item.name} />}
                                    </div>
                                ) : (
                                    <input
                                        type="color"
                                        value={item.color}
                                        className={styles.colorInput}
                                        onChange={(e) => handleItemChange(index, 'color', e.target.value)}
                                    />
                                )}
                                <input
                                    type="text"
                                    value={item.name}
                                    className={styles.nameInput}
                                    onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                />
                                <div className={styles.actionsCell}>
                                    {activeTab === 'roads' ? (
                                        <select
                                            value={item.lineType || 'solid'}
                                            className={styles.styleSelect}
                                            onChange={(e) => handleItemChange(index, 'lineType', e.target.value)}
                                        >
                                            <option value="solid">Сплошная</option>
                                            <option value="dashed">Пунктирная</option>
                                            <option value="dotted">Точечная</option>
                                        </select>
                                    ) : activeTab === 'plots' || activeTab === 'buildings' ? (
                                        <select
                                            value={item.hatch || 'none'}
                                            className={styles.styleSelect}
                                            onChange={(e) => handleItemChange(index, 'hatch', e.target.value)}
                                        >
                                            <option value="none">Нет</option>
                                            <option value="horizontal">Горизонтальная</option>
                                            <option value="vertical">Вертикальная</option>
                                            <option value="diagonal">Диагональная</option>
                                            <option value="cross">Крест</option>
                                            <option value="diagcross">Диагональный крест</option>
                                        </select>
                                    ) : activeTab === 'markers' ? (
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                setUploadIndex(index);
                                                fileInputRef.current?.click();
                                            }}
                                            title="Загрузить новое изображение"
                                        >
                                            <ImportIcon size={16} /> Загрузить
                                        </Button>
                                    ) : null }
                                </div>
                                <div className={styles.actionsCell}>
                                    <Button variant="icon" onClick={() => handleRemoveItem(index)} title="Удалить">
                                        <TrashIcon size={16} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <Button variant="secondary" onClick={handleAddItem} className={styles.addButton}>
                            <PlusIcon size={14} /> Добавить категорию
                        </Button>
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept="image/png, image/jpeg, image/svg+xml"
                        onChange={handleImageUpload}
                    />
                </div>
                <div className={styles.footer}>
                    <Button variant="default" onClick={onClose}>Отмена</Button>
                    <Button variant="primary" onClick={handleSaveChanges}>
                        <SaveIcon size={16} /> Сохранить изменения
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default LegendManager;